import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { postsApi, mediaUrl } from '../services/postsApi';

const REACTIONS = ['🙏', '❤️'];

/**
 * "Testimony of the Week" — a highlighted card pinned to the very top of the
 * Community feed, showing the single testimony a super-admin has featured.
 */
const FeaturedTestimonyCard = forwardRef(({ language = 'en', onOpen }, ref) => {
  const es = language === 'es';
  const [post, setPost] = useState(null);

  const load = async () => {
    try { setPost(await postsApi.featured()); } catch { setPost(null); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('lumera:featured-changed', h);
    return () => window.removeEventListener('lumera:featured-changed', h);
  }, []);
  useImperativeHandle(ref, () => ({ reload: load }));

  const react = async (e, emoji) => {
    e.stopPropagation();
    try {
      const res = await postsApi.react(post.id, emoji);
      setPost((p) => ({ ...p, reactions: res.reactions, my_reaction: res.my_reaction }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || (es ? 'Inicia sesión para reaccionar' : 'Sign in to react'));
    }
  };

  if (!post) return null;

  return (
    <Card
      onClick={() => onOpen?.(post)}
      className="overflow-hidden cursor-pointer border-amber-400/60 ring-1 ring-amber-400/40 bg-gradient-to-b from-amber-50/60 to-transparent dark:from-amber-950/20"
      data-testid="featured-testimony-card"
    >
      <div className="flex items-center gap-2 px-3 pt-3 text-amber-600">
        <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
        <span className="text-xs font-semibold uppercase tracking-widest">
          {es ? 'Testimonio de la semana' : 'Testimony of the Week'}
        </span>
      </div>
      <div className="flex items-center gap-3 p-3 pt-2">
        <Avatar className="w-9 h-9">
          {post.author_picture ? <AvatarImage src={post.author_picture} /> : null}
          <AvatarFallback className="text-xs bg-primary/15">{(post.author_name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-semibold leading-tight">{post.author_name}</p>
          {post.verse_reference && <p className="text-[11px] text-muted-foreground">{post.verse_reference}</p>}
        </div>
      </div>
      <img src={mediaUrl(post.image_url)} alt={post.caption || 'testimony'} className="w-full aspect-square object-cover bg-muted" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      {post.caption && <p className="text-sm p-3"><span className="font-semibold mr-1.5">{post.author_name}</span>{post.caption}</p>}
      <div className="flex items-center gap-2 px-3 pb-3" data-testid="featured-reactions">
        {REACTIONS.map((emoji) => {
          const active = post.my_reaction === emoji;
          const count = (post.reactions || {})[emoji] || 0;
          return (
            <button
              key={emoji}
              onClick={(e) => react(e, emoji)}
              className={`flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors ${active ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : 'border-border hover:bg-muted'}`}
              data-testid={`featured-react-${emoji === '🙏' ? 'pray' : 'love'}`}
            >
              <span>{emoji}</span>
              {count > 0 && <span className="text-xs font-medium text-muted-foreground">{count}</span>}
            </button>
          );
        })}
      </div>
    </Card>
  );
});

FeaturedTestimonyCard.displayName = 'FeaturedTestimonyCard';
export default FeaturedTestimonyCard;
